import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { LoadingButton } from "@material-ui/lab";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import Label from "components/Label";
import useWebotServer from "hooks/useWebotServer";
import useWhatsappNumbers from "hooks/useWhatsappNumbers";
import NumberSwitcher from "components/_dashboard/whatsappOfficial/NumberSwitcher";
import { CONSENT_ATTESTATION_TEXT } from "components/_dashboard/whatsappOfficial/campaigns/consentAttestation";
import { DEFAULT_RECENCY_MINUTES } from "components/_dashboard/whatsappOfficial/campaigns/campaignLabels";
import RecencySkipControl from "components/_dashboard/whatsappOfficial/campaigns/RecencySkipControl";
import { isWebotServerError } from "utils/contacts/contactWriteErrors";
import { PATH_DASHBOARD } from "routes/paths";
import ListingSharePreviewBubble from "./ListingSharePreviewBubble";
import type { REST_schema } from "../../../../@types/sharedSchema";

export type ListingShareRecipient = { contactId: string; displayName: string };

type Props = {
  open: boolean;
  onClose: () => void;
  listingId: string;
  recipients: ListingShareRecipient[];
  /** The send landed (campaign created). The host clears its selection. */
  onSent?: (result: REST_schema.WhatsappListingShareSendResponse) => void;
};

/** While Meta reviews a freshly created template (usually minutes), re-ask this often. */
const PENDING_POLL_MS = 15_000;

/**
 * The statuses worth waiting on. Anything else (REJECTED, DISABLED, PAUSED, LIMIT_EXCEEDED…) will
 * not resolve by itself in the next minutes, so it gets its own message and no polling.
 */
const WAITABLE_STATUSES: ReadonlySet<string> = new Set(["PENDING", "IN_REVIEW", "MISSING"]);

const notReadyMessage = (templateStatus: string): string => {
  if (templateStatus === "MISSING") return "מכינים את תבנית שיתוף הנכס במספר הזה ושולחים אותה לאישור של Meta. אפשר להשאיר את החלון פתוח — נבדוק שוב אוטומטית.";
  if (templateStatus === "PENDING" || templateStatus === "IN_REVIEW")
    return "תבנית שיתוף הנכס ממתינה לאישור של Meta — בדרך כלל זה לוקח כמה דקות. אפשר להשאיר את החלון פתוח, נבדוק שוב אוטומטית.";
  if (templateStatus === "PAUSED") return "Meta השהתה זמנית את תבנית שיתוף הנכס במספר הזה (בגלל איכות). נסו שוב בעוד כמה שעות.";
  return "תבנית שיתוף הנכס אינה זמינה במספר הזה כרגע. פנו לתמיכה ונטפל בזה.";
};

const errorText = (err: unknown, fallback: string): string =>
  (isWebotServerError(err) && err.code) || fallback;

/**
 * שליחת נכס בוואטסאפ רשמי — the confirm step.
 *
 * Everything that decides what actually goes out is server-side (the listing is placed in each
 * recipient's minisite, then a one-template campaign runs through the blocked list, consent
 * record and pacing). This dialog shows exactly what the customer will receive, names who it goes
 * to, collects the consent attestation verbatim, and waits honestly while Meta is still
 * reviewing the template.
 */
export default function ListingShareOfficialDialog({ open, onClose, listingId, recipients, onSent }: Props): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { whatsappListingShareStatus, whatsappListingShareSend } = useWebotServer();
  const { numbers, selectedKey, selectNumber, loading: numbersLoading } = useWhatsappNumbers();

  const [status, setStatus] = useState<REST_schema.WhatsappListingShareStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [consent, setConsent] = useState(false);
  const [recencyMinutes, setRecencyMinutes] = useState(DEFAULT_RECENCY_MINUTES);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<REST_schema.WhatsappListingShareSendResponse | null>(null);
  const requestSeq = useRef(0);
  // The first recipient drives the preview link (stable string, so the effect does not refire on
  // every render of the host).
  const previewContactId = recipients[0]?.contactId;

  const loadStatus = useCallback(async () => {
    if (!selectedKey) return;
    const seq = ++requestSeq.current;
    setLoadingStatus(true);
    setStatusError(null);
    // The previous number's answer must not stay on screen (or be sent from) while this loads.
    setStatus((current) => (current && current.numberKey !== selectedKey ? null : current));
    try {
      const next = await whatsappListingShareStatus({
        listingId,
        numberKey: selectedKey,
        ...(previewContactId ? { previewContactId } : {})
      });
      if (seq === requestSeq.current) setStatus(next);
    } catch (err) {
      if (seq === requestSeq.current) setStatusError(errorText(err, "לא הצלחנו לבדוק את תבנית השליחה. נסו שוב."));
    } finally {
      if (seq === requestSeq.current) setLoadingStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, selectedKey, previewContactId]);

  // Fresh state every time the dialog opens — a previous run's result must not greet the next one.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setSendError(null);
    setConsent(false);
    setRecencyMinutes(DEFAULT_RECENCY_MINUTES);
    setStatus(null);
  }, [open]);

  useEffect(() => {
    if (open && selectedKey) void loadStatus();
  }, [open, selectedKey, loadStatus]);

  // Poll while the template is under review, and stop the moment it is sendable or we close.
  useEffect(() => {
    if (!open || !status || status.sendable || result || !WAITABLE_STATUSES.has(status.templateStatus)) return undefined;
    const timer = window.setInterval(() => void loadStatus(), PENDING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, status, result, loadStatus]);

  const handleSend = async (): Promise<void> => {
    if (!status?.sendable || !consent) return;
    setSending(true);
    setSendError(null);
    try {
      const response = await whatsappListingShareSend({
        listingId,
        numberKey: status.numberKey,
        contactIds: recipients.map((recipient) => recipient.contactId),
        skipIfMessagedWithinMinutes: recencyMinutes,
        consentAttestationText: CONSENT_ATTESTATION_TEXT,
        consentAttestationAccepted: true
      });
      setResult(response);
      onSent?.(response);
    } catch (err) {
      setSendError(errorText(err, "השליחה נכשלה. נסו שוב בעוד רגע."));
    } finally {
      setSending(false);
    }
  };

  // Closes on a backdrop click like every dashboard dialog — except mid-send, when closing would
  // hide the only receipt of what went out.
  const handleClose = (): void => {
    if (sending) return;
    onClose();
  };

  // The preview shows the REAL link the first recipient gets (resolved server-side with the same
  // minisite choice the send makes) — a placeholder read as "the general minisite link".
  const previewRecipient = recipients[0];
  const previewLinkText = status?.previewLink ?? `https://app.webot.co.il/myListings/…/${listingId}`;
  const shownRecipients = recipients.slice(0, 8);
  const skipped = [...(result?.skippedAtCreate ?? []), ...(result?.minisiteFailures ?? [])];
  const queued = result?.totals?.queued ?? 0;

  const renderBody = (): JSX.Element => {
    if (numbersLoading) {
      return (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      );
    }
    if (numbers.length === 0) {
      return (
        <Alert severity="info">
          עדיין אין מספר WhatsApp רשמי מחובר לחשבון.{" "}
          <Link href={PATH_DASHBOARD.general.whatsappOfficial} target="_blank" rel="noopener">
            לחיבור מספר
          </Link>
        </Alert>
      );
    }
    if (result) {
      return (
        <Stack spacing={2}>
          {result.activation?.ok === false ? (
            <Alert severity="warning">{result.activation.error ?? "השליחה נוצרה אך לא הופעלה."}</Alert>
          ) : (
            <Alert severity="success">
              {queued > 0
                ? `${queued} הודעות נכנסו לתור השליחה, והנכס כבר מופיע במיניסייט של כל אחד מהם. מי שחסום לדיוור ידולג אוטומטית — הסטטוס המלא בעמוד המעקב.`
                : "אף הודעה לא נכנסה לתור השליחה."}
            </Alert>
          )}
          {skipped.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                לא נשלח ל-{skipped.length}:
              </Typography>
              <Stack spacing={0.25}>
                {skipped.slice(0, 8).map((entry) => (
                  <Typography key={`${entry.phone}|${entry.reason}`} variant="caption" color="text.secondary">
                    <Box component="span" dir="ltr">
                      {recipients.find((r) => r.contactId === entry.phone)?.displayName ?? entry.phone}
                    </Box>{" "}
                    · {entry.reason}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
          {/* A new tab: this dialog sits inside the listing modal, which a route change would
              leave open on top of the page it navigated to. */}
          <Link href={PATH_DASHBOARD.general.whatsappCampaignDetail(result.campaignId)} target="_blank" rel="noopener">
            מעקב אחרי השליחה
          </Link>
        </Stack>
      );
    }

    return (
      <Stack spacing={2.5}>
        {numbers.length > 1 && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              שליחה מהמספר
            </Typography>
            <NumberSwitcher numbers={numbers} selectedKey={selectedKey} onSelect={selectNumber} />
          </Stack>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {recipients.length === 1 ? "נשלח ללקוח אחד" : `נשלח ל-${recipients.length} לקוחות`}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {shownRecipients.map((recipient) => (
              <Label key={recipient.contactId}>{recipient.displayName}</Label>
            ))}
            {recipients.length > shownRecipients.length && <Label>+{recipients.length - shownRecipients.length}</Label>}
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            כך ההודעה תיראה
          </Typography>
          {loadingStatus && !status ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : status ? (
            <Box sx={{ bgcolor: "#E5DDD5", borderRadius: 2, p: 1.5 }}>
              <ListingSharePreviewBubble
                body={status.previewBody}
                link={previewLinkText}
                footer={status.previewFooter}
                headerImageUrl={status.headerImageUrl}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {previewRecipient
                  ? status.previewLink
                    ? `זה הקישור ש${previewRecipient.displayName} יקבל — ישר לנכס בתוך המיניסייט שלו. כל לקוח מקבל קישור אישי משלו.`
                    : `ל${previewRecipient.displayName} אין עדיין מיניסייט — ייפתח לו מיניסייט עם הנכס, והקישור יוביל ישר לנכס. כל לקוח מקבל קישור אישי משלו.`
                  : "כל לקוח מקבל קישור אישי לנכס בתוך המיניסייט שלו."}
              </Typography>
            </Box>
          ) : null}
        </Box>

        {statusError && <Alert severity="error">{statusError}</Alert>}
        {status && !status.sendable && (
          <Alert
            severity={WAITABLE_STATUSES.has(status.templateStatus) ? "info" : "warning"}
            {...(WAITABLE_STATUSES.has(status.templateStatus) ? { icon: <CircularProgress size={18} /> } : {})}
          >
            {notReadyMessage(status.templateStatus)}
          </Alert>
        )}

        <RecencySkipControl value={recencyMinutes} onChange={setRecencyMinutes} disabled={sending} />

        <FormControlLabel
          sx={{ alignItems: "flex-start", mx: 0 }}
          control={<Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)} sx={{ mt: -0.75 }} />}
          label={<Typography variant="body2">{CONSENT_ATTESTATION_TEXT}</Typography>}
        />
        {sendError && <Alert severity="error">{sendError}</Alert>}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WhatsAppIcon sx={{ color: "#25D366" }} />
          <span>שליחת הנכס בוואטסאפ רשמי</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={sending}>
          {result ? "סגירה" : "ביטול"}
        </Button>
        {!result && numbers.length > 0 && (
          <LoadingButton
            variant="contained"
            loading={sending}
            disabled={!status?.sendable || loadingStatus || status.numberKey !== selectedKey || !consent || recipients.length === 0}
            onClick={() => void handleSend()}
            startIcon={<WhatsAppIcon />}
          >
            שליחה
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
